import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Input,
  Row,
  Col,
  Modal,
} from 'antd';
import PropTypes from 'prop-types';

import WorkflowCard from './WorkflowCard';
import WorkflowDetails from './WorkflowDetails';
import { workflowMap } from './workflowMarketplace.constants';
import WorkflowMarketplaceImportModal from './WorkflowMarketplaceImportModal';
import { includesTerm } from '../helpers/helpers';
import Permissions from '../auth/Permissions';
import { updateLastSeenMarketplace } from '../users/state/users.actions';
import { importWorkflowMarketplaceWorkflow } from './state/workflowMarketplace.actions';
import { getNotifications } from '../notifications/state/notifications.actions';
import WorkflowMarketplaceLoadingContainer from './WorkflowMarketplaceLoadingContainer';
import WorkflowMarketplaceSuccessContainer from './WorkflowMarketplaceSuccessContainer';
import WorkflowMarketPlaceErrorContainer from './WorkflowMarketplaceErrorContainer';

const BASE_CATEGORIES = [
  'All',
  'Recently Added',
  'Already Imported',
];

let didReset = false;

export default function WorkflowMarketplace({ open, onClose }) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const workflows = useSelector((state) => state.workflowMarketplace.workflows);

  const relevantUser = useMemo(() => (
    users.find((user) => user.id === Permissions.id)
  ), [users, Permissions.id]);

  useEffect(() => {
    if (open && relevantUser?.id) {
      dispatch(updateLastSeenMarketplace(relevantUser.id));
      didReset = false;
    }
  }, [open, relevantUser?.id]);

  const [searchTerm, setSearchTerm] = useState('');
  const [relevantWorkflow, setRelevantWorkflow] = useState(null);
  const [importWorkflow, setImportWorkflow] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(false);
  const [loadingWorkflow, setLoadingWorkflow] = useState(null);
  const [successWorkflow, setSuccessWorkflow] = useState(null);
  const [errorWorkflow, setErrorWorkflow] = useState(null);

  if (!open && !didReset) {
    setSuccessWorkflow(null);
    setErrorWorkflow(null);
    setImportWorkflow(null);
    setLoadingWorkflow(null);
    setRelevantWorkflow(null);
    setSelectedCategory('All');
    setSearchTerm('');
    setLoading(false);
    didReset = true;
  }

  const executeImportWorkflow = async (workflow, divisions) => {
    if (loading || !Permissions.has('IMPORT_WORKFLOW_MARKETPLACE')) return;
    setLoading(true);
    setImportWorkflow(null);
    setLoadingWorkflow(workflow);

    const result = await dispatch(importWorkflowMarketplaceWorkflow({
      id: workflow.id,
      name: workflow.name,
      divisions,
    }));

    if (result) {
      setSuccessWorkflow(workflow.name);
      dispatch(getNotifications());
    } else {
      setErrorWorkflow({
        workflow,
        divisions,
      });
    }
    setLoading(false);
    setLoadingWorkflow(null);
  };

  const sortedWorkflowCategories = Array.from(
    new Set(
      Object.values(workflowMap).map((workflow) => workflow.categories).flat(),
    ),
  ).sort(
    (a, b) => a.localeCompare(b),
  );

  const categories = new Set([
    ...BASE_CATEGORIES,
    ...sortedWorkflowCategories,
  ]);

  const relevantWorkflows = useMemo(() => {
    const mappedWorkflows = workflows.map((workflow) => ({
      ...workflow,
      ...workflowMap[workflow.name],
    }));

    let filteredWorkflows;

    switch (selectedCategory) {
      case 'All':
        filteredWorkflows = mappedWorkflows;
        break;
      case 'Recently Added':
        filteredWorkflows = mappedWorkflows.filter(
          (workflow) => workflow.recentlyAdded,
        );
        break;
      case 'Already Imported':
        filteredWorkflows = mappedWorkflows.filter((workflow) => workflow.imported);
        break;
      default:
        filteredWorkflows = mappedWorkflows.filter((workflow) => workflow.categorySet.has(selectedCategory));
        break;
    }

    return filteredWorkflows.filter((workflow) => includesTerm(workflow.name, searchTerm));
  }, [selectedCategory, workflows, searchTerm]);

  if (!open) return null;

  return (
    <>
      <Modal
        visible={open}
        onCancel={onClose}
        footer={null}
        width={1600}
        centered
        wrapClassName="workflow-marketplace-modal-wrap"
        className="workflow-marketplace-modal"
      >
        <Row style={{ height: '100%' }}>
          <Col span={4}>
            <div className="workflow-marketplace-sidebar">
              <div className="workflow-marketplace-sidebar-title">BY CATEGORIES</div>
              <div className="workflow-marketplace-sidebar-categories">
                {Array.from(categories).map((category) => (
                  <div
                    key={category}
                    className={`workflow-marketplace-sidebar-category ${selectedCategory === category ? 'selected' : ''}`}
                    onClick={() => setSelectedCategory(category)}>
                    {category}
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col span={20}>
            <Row className="workflow-marketplace-header">
              <div className="workflow-marketplace-header-title">Import ready-made workflows</div>
              <Input.Search
                className="workflow-marketplace-search"
                placeholder="Search workflows..."
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                value={searchTerm}
              />
            </Row>
            <Row className="workflow-marketplace-container" gutter={[16, 16]}>
              {relevantWorkflows.map((workflow) => (
                <Col key={workflow.id} span={8}>
                  <WorkflowCard
                    workflow={workflow}
                    setRelevantWorkflow={setRelevantWorkflow}
                    setImportWorkflow={setImportWorkflow}
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        <WorkflowMarketplaceLoadingContainer
          onClose={() => setLoading(false)}
          workflow={loadingWorkflow}
        />
        <WorkflowMarketPlaceErrorContainer
          onClose={() => setErrorWorkflow(null)}
          workflow={errorWorkflow}
          importWorkflow={executeImportWorkflow}
        />
      </Modal>
      <WorkflowMarketplaceSuccessContainer
        onClose={() => setSuccessWorkflow(null)}
        workflow={successWorkflow}
      />
      <WorkflowDetails
        open={!!relevantWorkflow}
        onClose={() => setRelevantWorkflow(null)}
        workflow={relevantWorkflow}
        setImportWorkflow={(workflow) => {
          setImportWorkflow(workflow);
          setRelevantWorkflow(null);
        }}
        relevantUser={relevantUser}
      />
      <WorkflowMarketplaceImportModal
        onClose={() => setImportWorkflow(null)}
        workflow={importWorkflow}
        importWorkflow={executeImportWorkflow}
        loading={loading}
        setLoading={setLoading}
      />
    </>
  );
}

WorkflowMarketplace.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
